import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HostInviteRsvpPage
 *
 * Thin redirector. The canonical client experience is always:
 *   envelope -> welcome -> intake -> confirmation
 * regardless of how the client got there. So this page fetches the
 * invite, resolves the event slug, and forwards to the standard
 * /e/:eventSlug flow with ?invite_token={token} so EventPageElegant
 * can pre-pick the host's offered slots on the Welcome step.
 */
export default function HostInviteRsvpPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const go = async () => {
      try {
        const res = await fetch(`${API}/rsvp/invite/${token}`);
        if (!res.ok) {
          navigate(`/?invite_invalid=1`);
          return;
        }
        const data = await res.json();
        // Resolve event slug or event_id; backend returns event_name and the
        // invite has event_id. We forward to /e/{event_id} (EventPageElegant
        // accepts both slug and event_id in :eventSlug).
        const slugOrId = data.event_slug || data.event_id || data.invite_id;
        // Fall back: the invite payload from /api/rsvp/invite/{token} does
        // not currently include event_id, so we hit a second endpoint to
        // resolve. Cheapest path: include event_id in the public invite read.
        navigate(`/e/${slugOrId}?invite_token=${encodeURIComponent(token)}`, { replace: true });
      } catch (_e) {
        navigate(`/?invite_invalid=1`);
      }
    };
    go();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="invite-redirect">
      <Loader2 className="w-6 h-6 animate-spin text-stone-500" />
    </div>
  );
}
